import { Component, EventEmitter, Input, Output } from '@angular/core'
import { MessageService } from 'primeng/api'

import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'

/** Lo mínimo del pago que el diálogo necesita para mostrarlo y corregirlo. */
export interface PaymentDateTarget {
  _id: string
  fecha: string | Date | null
  monto?: number | null
  trackingKey?: string | null
  cliente?: string | null
}

/**
 * Corrige la fecha de un pago a la del depósito real. La fecha decide los días de retraso de cada
 * letra y la calificación del cliente, así que no se edita en silencio: el motivo es obligatorio y
 * el backend sella la fecha anterior y la nueva en la bitácora del pago y de sus arrendamientos.
 */
@Component({
  selector: 'app-payment-date-dialog',
  templateUrl: './payment-date-dialog.component.html',
  styleUrls: ['./payment-date-dialog.component.scss'],
})
export class PaymentDateDialogComponent {
  @Input() set payment(p: PaymentDateTarget | null) {
    this.target = p
    this.newDate = p?.fecha ? this.toLocalDate(p.fecha) : null
    this.reason = ''
    this.error = ''
  }

  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()

  /** Avisa a la pantalla para que recargue lo que muestra. */
  @Output() saved = new EventEmitter<void>()

  target: PaymentDateTarget | null = null
  newDate: Date | null = null
  reason = ''
  error = ''
  saving = false

  /** Nadie deposita en el futuro: la fecha máxima es hoy. */
  readonly hoy = new Date()

  constructor(
    private apiPayment: ApiPaymentService,
    private messageService: MessageService,
  ) {}

  /** Un motivo de dos letras no le explica nada a quien lo lea después. */
  get reasonValid(): boolean {
    return this.reason.trim().length >= 10
  }

  get changed(): boolean {
    if (!this.newDate || !this.target?.fecha) return !!this.newDate
    return this.isoDay(this.newDate) !== this.isoDay(this.toLocalDate(this.target.fecha))
  }

  get canSave(): boolean {
    return !!this.newDate && this.changed && this.reasonValid && !this.saving
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }

  save(): void {
    if (!this.canSave || !this.target || !this.newDate) return
    this.saving = true
    this.error = ''
    this.apiPayment.changePaymentDate(this.target._id, this.isoDay(this.newDate), this.reason.trim()).subscribe((r) => {
      this.saving = false
      if (r.error) {
        this.error = r.msg
        return
      }
      this.messageService.add({ severity: 'success', summary: 'Fecha corregida', detail: 'Quedó registrada en la bitácora del pago y de sus arrendamientos.', life: 5000 })
      this.saved.emit()
      this.close()
    })
  }

  //* La fecha del pago viaja como medianoche UTC: leerla en hora local la corre al día anterior.
  private toLocalDate(v: string | Date): Date {
    const d = new Date(v)
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  }

  private isoDay(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
}
